<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed } from 'vue'
import { useAdminAuthStore } from '@/stores/auth'
import BaseButton from '@shared/components/BaseButton.vue'
import BaseModal from '@shared/components/BaseModal.vue'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Admin access token lifetime in milliseconds (15 minutes). */
const TOKEN_LIFETIME_MS = 15 * 60 * 1000

/** Show warning this many ms before expiry (2 minutes). */
const WARNING_BEFORE_EXPIRY_MS = 2 * 60 * 1000

/** Interval for countdown updates (1 second). */
const TICK_INTERVAL_MS = 1000

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const authStore = useAdminAuthStore()

const showWarning = ref(false)
const secondsRemaining = ref(0)
const extending = ref(false)

let warningTimer: ReturnType<typeof setTimeout> | null = null
let expiryTimer: ReturnType<typeof setTimeout> | null = null
let countdownInterval: ReturnType<typeof setInterval> | null = null
let tokenIssuedAt: number | null = null

// ---------------------------------------------------------------------------
// Computed
// ---------------------------------------------------------------------------

const formattedTime = computed(() => {
  const mins = Math.floor(secondsRemaining.value / 60)
  const secs = secondsRemaining.value % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
})

// ---------------------------------------------------------------------------
// Timer management
// ---------------------------------------------------------------------------

function clearAllTimers() {
  if (warningTimer) {
    clearTimeout(warningTimer)
    warningTimer = null
  }
  if (expiryTimer) {
    clearTimeout(expiryTimer)
    expiryTimer = null
  }
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
}

function startTimers() {
  clearAllTimers()

  tokenIssuedAt = Date.now()
  const warningDelay = TOKEN_LIFETIME_MS - WARNING_BEFORE_EXPIRY_MS
  const expiryDelay = TOKEN_LIFETIME_MS

  // Timer to show warning 2 minutes before expiry
  warningTimer = setTimeout(() => {
    showWarning.value = true
    secondsRemaining.value = Math.floor(WARNING_BEFORE_EXPIRY_MS / 1000)

    // Start countdown
    countdownInterval = setInterval(() => {
      secondsRemaining.value = Math.max(0, secondsRemaining.value - 1)
    }, TICK_INTERVAL_MS)
  }, warningDelay)

  // Timer for auto-logout on expiry
  expiryTimer = setTimeout(() => {
    showWarning.value = false
    clearAllTimers()
    authStore.logout()
  }, expiryDelay)
}

// ---------------------------------------------------------------------------
// Visibility change handling — pause/resume timers when tab is hidden/visible
// ---------------------------------------------------------------------------

function handleVisibilityChange() {
  if (document.hidden || !authStore.isAuthenticated || tokenIssuedAt === null) return

  // Recalculate remaining time on tab becoming visible
  const elapsed = Date.now() - tokenIssuedAt
  const remaining = TOKEN_LIFETIME_MS - elapsed

  if (remaining <= 0) {
    // Token has expired while tab was hidden
    showWarning.value = false
    clearAllTimers()
    authStore.logout()
    return
  }

  if (remaining <= WARNING_BEFORE_EXPIRY_MS) {
    // Should be showing warning
    showWarning.value = true
    secondsRemaining.value = Math.floor(remaining / 1000)

    clearAllTimers()

    // Restart countdown
    countdownInterval = setInterval(() => {
      secondsRemaining.value = Math.max(0, secondsRemaining.value - 1)
    }, TICK_INTERVAL_MS)

    // Set expiry timer for remaining time
    expiryTimer = setTimeout(() => {
      showWarning.value = false
      clearAllTimers()
      authStore.logout()
    }, remaining)
  }
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

async function extendSession() {
  extending.value = true
  try {
    await authStore.refreshToken()
    showWarning.value = false
    // Restart timers with fresh token
    startTimers()
  } catch {
    // If refresh fails, log out
    showWarning.value = false
    clearAllTimers()
    await authStore.logout()
  } finally {
    extending.value = false
  }
}

async function handleLogout() {
  showWarning.value = false
  clearAllTimers()
  await authStore.logout()
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

// Watch auth state to start/stop timers
watch(
  () => authStore.isAuthenticated,
  (isAuth) => {
    if (isAuth) {
      startTimers()
    } else {
      showWarning.value = false
      clearAllTimers()
    }
  },
  { immediate: true },
)

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  clearAllTimers()
})
</script>

<template>
  <BaseModal
    :visible="showWarning"
    header="Session Expiring Soon"
    width="400px"
    :closable="false"
    @update:visible="(val: boolean) => { if (!val) showWarning = false }"
  >
    <div class="text-center" data-testid="session-timeout-dialog">
      <!-- Icon -->
      <div class="mb-4">
        <div class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-warning/10">
          <i class="pi pi-clock text-2xl text-warning" aria-hidden="true" />
        </div>
      </div>

      <!-- Message -->
      <p
        id="session-timeout-message"
        class="text-sm text-text-secondary mb-2"
      >
        Your session will expire in
      </p>

      <!-- Countdown -->
      <p
        class="text-2xl font-mono font-bold text-warning mb-6"
        data-testid="session-timeout-countdown"
      >
        {{ formattedTime }}
      </p>

      <!-- Actions -->
      <div class="flex flex-col gap-3" data-testid="session-timeout-actions">
        <BaseButton
          variant="primary"
          :loading="extending"
          :disabled="extending"
          data-testid="session-timeout-extend-btn"
          @click="extendSession"
        >
          Extend Session
        </BaseButton>
        <BaseButton
          variant="secondary"
          data-testid="session-timeout-logout-btn"
          @click="handleLogout"
        >
          Logout
        </BaseButton>
      </div>
    </div>
  </BaseModal>
</template>
